import { CommonModule } from '@angular/common';
import {
  Component,
  effect,
  EventEmitter,
  inject,
  Injector,
  Input,
  OnDestroy,
  OnInit,
  Output,
  runInInjectionContext,
  signal,
  Type,
  WritableSignal,
  ChangeDetectionStrategy,
} from '@angular/core';
import { AbstractControl } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { FormlyFieldConfig } from '@ngx-formly/core';
import { Subscription } from 'rxjs';
import {
  FIELD_TYPES,
  getFieldTypeIcons,
  resolveFieldTypeAlias,
} from '../../core/config/field-types.config';
import { LoggerService } from '../../core/services/logger.service';
import { ModalConfig, ModalService } from '../../core/services/modal.service';
import { DragHandleDirective } from '../../shared/directives/drag-handle.directive';
import { DragItemDirective } from '../../shared/directives/drag-item.directive';
import { DragListDirective, DragReorderEvent } from '../../shared/directives/drag-list.directive';
import { TippyDirective } from '../../shared/directives/tippy.directive';
import { TranslatePipe } from '../../shared/pipes/translate.pipe';
import { GenericFieldConfigEditorComponent } from './fields/generic/generic-field-config-editor.component';

import { BreadcrumbService } from '../../core/services/breadcrumb.service';
import { TranslationService } from '../../core/services/translation.service';
import { ButtonComponent } from '../../shared/components/button/button.component';
import { IconComponent } from '../../shared/components/icon/icon.component';
import { RemoveFieldConfirmationComponent } from './remove-field-confirmation.component';

interface FieldOption {
  label: string;
  value: string;
  description?: string;
  isDefault?: boolean;
  metricAmount?: number;
}

type CalculationKind =
  | 'date_diff_between_fields'
  | 'date_diff_from_previous'
  | 'arithmetic_two_fields'
  | 'checklist_total_metric'
  | 'state_transition';

type ArithmeticOperator = 'add' | 'subtract' | 'multiply' | 'divide';
type EmptyBehavior = 'null' | 'zero';
type DurationUnit = 'seconds' | 'minutes' | 'hours' | 'days';
type DurationDisplayFormat = 'short' | 'long';

interface FieldDefinition {
  id: string;
  type: string;
  label: string;
  placeholder?: string;
  required?: boolean;
  rows?: number; // For textarea
  options?: FieldOption[]; // For radio/checkbox-group fields
  /** Controls where options come from: inline array or a shared lookup list */
  optionsSource?: 'inline' | 'lookup-list';
  /** When optionsSource === 'lookup-list', the ID of the referenced LookupList */
  lookupListId?: string;
  allowMultiple?: boolean; // For checkbox-group - allow multiple selections (defaults to true)
  // Photo field options
  allowMultiplePhotos?: boolean; // For photo fields - allow multiple photos
  filenamePrefix?: string; // For photo fields - custom filename prefix for downloads
  // Numeric field options (number, range)
  min?: number; // Minimum value
  max?: number; // Maximum value
  step?: number; // Step increment
  // Pattern validation (email, tel, url)
  pattern?: string; // Regex pattern for validation
  // Text label field options
  content?: string; // Markdown content for text-label fields (separate from label)
  fontSize?: string; // CSS font size
  fontWeight?: string; // CSS font weight
  color: string; // CSS color (required, initialized to '#333')
  textAlign?: string; // CSS text alignment
  tag?: string; // Semantic tag (div, span, h2, p, etc.)
  // Layout options
  width?: 'auto' | 'full'; // How wide the field spans in the rendered form
  /** Per-field pre-fill override. 'default'/undefined → use form-level default. */
  prefillLastValue?: 'default' | 'always' | 'never';
  calculationKind?: CalculationKind;
  inputFieldAId?: string;
  inputFieldBId?: string;
  currentFieldId?: string;
  checklistFieldId?: string;
  sourceFieldId?: string;
  outputFieldId?: string;
  transitions?: Array<{ from?: string[] | string; to?: string; label?: string; value?: string }>;
  transitionFallback?: string;
  arithmeticOperator?: ArithmeticOperator;
  numberFormat?: string;
  durationFormat?: string;
  emptyBehavior?: EmptyBehavior;
  persistSnapshot?: true;
  durationInputUnit?: DurationUnit;
  durationDisplayFormat?: DurationDisplayFormat;
  visibleWhen?: {
    fieldId: string;
    values: string[] | string;
    /** Temporary editor state: selected values when origin field has known options (multi-checkbox). Not persisted. */
    selectedValues?: string[];
  };
  visibleWhenMode?: 'none' | 'show_when_value';
}

@Component({
  selector: 'app-form-builder',
  standalone: true,
  imports: [
    CommonModule,
    TranslatePipe,
    IconComponent,
    ButtonComponent,
    TippyDirective,
    DragListDirective,
    DragItemDirective,
    DragHandleDirective,
  ],
  templateUrl: './form-builder.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './form-builder.component.css',
})
export class FormBuilderComponent implements OnInit, OnDestroy {
  private logger = inject(LoggerService).getLogger('forms');
  private modalService = inject(ModalService);
  private injector = inject(Injector);
  private translationService = inject(TranslationService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private breadcrumbService = inject(BreadcrumbService);

  @Input() initialFields: FormlyFieldConfig[] = [];
  /** Initial column count for the rendered form; 1 = single column (default). */
  @Input() initialColumns: 1 | 2 | 3 | 4 = 1;
  @Output() fieldsChanged = new EventEmitter<FormlyFieldConfig[]>();
  /** Emits whenever the user changes the form-level column count. */
  @Output() columnsChanged = new EventEmitter<1 | 2 | 3 | 4>();

  fields = signal<FieldDefinition[]>([]);
  /** Current column count selection — drives the column-picker UI. */
  columns = signal<1 | 2 | 3 | 4>(1);

  /**
   * Signal-based modal button state management using the writable signal pattern
   *
   * When opening a field editor modal:
   * 1. Create fieldSignal with a copy of the field
   * 2. Create saveDisabled and cancelDisabled signals
   * 3. Pass fieldSignal directly to the editor component via componentInputs
   * 4. Editor updates fieldSignal during normal editing
   * 5. Parent save/cancel buttons check saveDisabled signal state
   * 6. On save, parent reads modified field directly from fieldSignal
   * 7. No need for child component methods like getModifiedField()
   */
  private modalSaveDisabled: WritableSignal<string | null> | null = null;
  private modalCancelDisabled: WritableSignal<string | null> | null = null;

  // Track which field item has its action buttons expanded (via drag-handle tap)
  activeMenuIndex = signal<number | null>(null);

  // ── URL-driven field editor modal state ──────────────────────────────────
  /** True while a field editor modal is open — prevents the queryParams subscription
   *  from re-opening the modal when we ourselves navigate to add ?field= */
  private _isModalOpen = false;
  /** Close signal for the topmost field editor modal; set to cancel from the outside
   *  (e.g. browser back) */
  private _currentModalCloseSignal: WritableSignal<{ action: string; data?: any } | null> | null =
    null;
  private _queryParamsSub: Subscription | null = null;

  // Field type configuration loaded from centralized source
  fieldTypeIcons = getFieldTypeIcons();

  /**
   * Get the editor component and Formly config fields for a given field type.
   *
   * All field types use the Formly-driven GenericFieldConfigEditorComponent.
   *
   * @param fieldType The field type (e.g., 'text', 'email', 'photo')
   * @returns Object with component class and configFormFields
   * @throws Error if no editFormDefinition is found for the field type
   */
  private getEditorComponentForFieldType(fieldType: string): {
    component: Type<any>;
    configFormFields: FormlyFieldConfig[];
  } {
    const fieldTypeConfig = FIELD_TYPES.find((ft) => ft.name === fieldType);

    if (!fieldTypeConfig?.editFormDefinition) {
      throw new Error(
        `No editFormDefinition found for field type "${fieldType}". ` +
          `Please ensure editFormDefinition is configured in field-types.config.ts.`,
      );
    }

    return {
      component: GenericFieldConfigEditorComponent,
      configFormFields: fieldTypeConfig.editFormDefinition,
    };
  }

  getFieldIcon(fieldType: string): string {
    return this.fieldTypeIcons[fieldType] || 'help-circle';
  }

  ngOnInit() {
    // Initialise columns from parent input
    this.columns.set(this.initialColumns);

    // Convert initial FormlyFieldConfig to FieldDefinition
    if (this.initialFields.length > 0) {
      const convertedFields = this.initialFields.map((field) => this.formlyToFieldDef(field));
      this.fields.set(convertedFields);
    }

    // Subscribe to query params to detect browser-back closing the field editor modal.
    // When ?field= is removed by a back navigation while the modal is open, we programmatically
    // close the modal via its close signal.
    this._queryParamsSub = this.route.queryParams.subscribe((params) => {
      const fieldId = params['field'];
      if (!fieldId && this._isModalOpen && this._currentModalCloseSignal) {
        // Browser back removed ?field= while modal is open — close it
        this._currentModalCloseSignal.set({ action: 'cancel' });
      }
    });

    // If the page was loaded/refreshed with ?field= already in the URL, reopen the modal.
    const initialFieldId = this.route.snapshot.queryParams['field'];
    if (initialFieldId) {
      this.openFieldByUrl(initialFieldId);
    }
  }

  ngOnDestroy(): void {
    this._queryParamsSub?.unsubscribe();
  }

  /** Called from the columns-picker UI; persists the choice and notifies the parent. */
  setColumns(cols: 1 | 2 | 3 | 4): void {
    this.columns.set(cols);
    this.columnsChanged.emit(cols);
  }

  private formlyToFieldDef(field: FormlyFieldConfig): FieldDefinition {
    const options = field.props?.['options'];
    const optionsArray = Array.isArray(options) ? options : [];

    // TEMPORARY BACKWARD COMPATIBILITY: Resolve any field type aliases to canonical names
    // This handles old data that uses deprecated field type names (e.g., 'input' → 'text')
    // Once proper data migration is done, this can be removed.
    const rawFieldType = field.type as string;
    const resolvedFieldType = resolveFieldTypeAlias(rawFieldType);

    // Map Formly types to our field types
    let fieldType = resolvedFieldType;
    let allowMultiple: boolean | undefined;

    if (resolvedFieldType === 'checkbox-group') {
      // Keep as checkbox-group - direct mapping
      allowMultiple = field.props?.['multiple'] !== false;
    } else if (resolvedFieldType === 'radio') {
      fieldType = 'radio';
    } else if (resolvedFieldType === 'select') {
      fieldType = 'select';
      allowMultiple = field.props?.['multiple'] || false;
    }

    const fieldDef: FieldDefinition = {
      id: (field as any).id || crypto.randomUUID(),
      type: fieldType,
      label: field.props?.['label'] || '',
      placeholder: field.props?.['placeholder'] || '',
      required: field.props?.['required'] || false,
      rows: field.props?.['rows'] || 3,
      options: optionsArray,
      // Extract optionsSource and lookupListId for lookup list configuration
      optionsSource: field.props?.['optionsSource'] as 'inline' | 'lookup-list' | undefined,
      lookupListId: field.props?.['lookupListId'] as string | undefined,
      allowMultiple: allowMultiple,
      allowMultiplePhotos: field.props?.['allowMultiplePhotos'] || false,
      filenamePrefix: field.props?.['filenamePrefix'] || '',
      // Extract numeric properties
      min: field.props?.['min'] as number | undefined,
      max: field.props?.['max'] as number | undefined,
      step: field.props?.['step'] as number | undefined,
      // Extract pattern (convert RegExp to string if needed)
      pattern: this.extractPattern(field.props?.['pattern']),
      content: field.props?.['content'] || '',
      fontSize: field.props?.['fontSize'] || 'md',
      fontWeight: field.props?.['fontWeight'] || 'normal',
      color: field.props?.['color'] || '#333',
      textAlign: field.props?.['textAlign'] || 'left',
      tag: field.props?.['tag'] || 'div',
      // Derive 'width' from className stored on the Formly config.
      // 'flex-basis-full' means the field should span the full row.
      width: (field.className || '').includes('flex-basis-full') ? 'full' : 'auto',
      // Per-field pre-fill override (undefined → inherit form-level default)
      prefillLastValue: field.props?.['prefillLastValue'] as
        | 'default'
        | 'always'
        | 'never'
        | undefined,
      calculationKind: field.props?.['calculationKind'] as CalculationKind | undefined,
      inputFieldAId: field.props?.['inputFieldAId'] as string | undefined,
      inputFieldBId: field.props?.['inputFieldBId'] as string | undefined,
      currentFieldId: field.props?.['currentFieldId'] as string | undefined,
      checklistFieldId: field.props?.['checklistFieldId'] as string | undefined,
      sourceFieldId: field.props?.['sourceFieldId'] as string | undefined,
      outputFieldId: field.props?.['outputFieldId'] as string | undefined,
      transitions: this.formlyTransitionsToEditorTransitions(
        field.props?.['transitions'] as Array<Record<string, unknown>> | undefined,
      ),
      transitionFallback: field.props?.['transitionFallback'] as string | undefined,
      arithmeticOperator: field.props?.['arithmeticOperator'] as ArithmeticOperator | undefined,
      numberFormat: field.props?.['numberFormat'] as string | undefined,
      durationFormat: field.props?.['durationFormat'] as string | undefined,
      emptyBehavior: field.props?.['emptyBehavior'] as EmptyBehavior | undefined,
      persistSnapshot:
        field.props?.['persistSnapshot'] === true ? (true as const) : (undefined as undefined),
      durationInputUnit: field.props?.['durationInputUnit'] as DurationUnit | undefined,
      durationDisplayFormat: field.props?.['durationDisplayFormat'] as
        | DurationDisplayFormat
        | undefined,
      visibleWhen: this.normalizeVisibleWhen(
        field.props?.['visibleWhen'] as { fieldId?: string; values?: unknown } | undefined,
      ),
      visibleWhenMode: this.normalizeVisibleWhen(
        field.props?.['visibleWhen'] as { fieldId?: string; values?: unknown } | undefined,
      )
        ? 'show_when_value'
        : 'none',
    };
    return fieldDef;
  }

  private extractPattern(pattern: string | RegExp | undefined): string | undefined {
    if (!pattern) return undefined;
    if (typeof pattern === 'string') return pattern;
    if (pattern instanceof RegExp) return pattern.source;
    return undefined;
  }

  private normalizeStateFromValues(from: unknown): string[] {
    if (Array.isArray(from)) {
      return from.map((value) => String(value ?? ''));
    }

    if (typeof from === 'string') {
      return from.split(',').map((value) => value.trim());
    }

    return [];
  }

  private formlyTransitionsToEditorTransitions(
    transitions: Array<Record<string, unknown>> | undefined,
  ): Array<{ from?: string[] | string; to?: string; label?: string; value?: string }> {
    if (!Array.isArray(transitions)) {
      return [];
    }

    return transitions.map((rule) => {
      const fromValues = this.normalizeStateFromValues(rule['from']);
      return {
        from: fromValues,
        to: String(rule['to'] ?? ''),
      };
    });
  }

  private editorTransitionsToFormlyTransitions(
    transitions:
      | Array<{ from?: string[] | string; to?: string; label?: string; value?: string }>
      | undefined,
  ): Array<{ from: string[]; to: string }> {
    if (!Array.isArray(transitions)) {
      return [];
    }

    return transitions
      .map((rule) => {
        const fromRaw = rule.from ?? rule.label ?? '';
        const toRaw = rule.to ?? rule.value ?? '';
        return {
          from: this.normalizeStateFromValues(fromRaw),
          to: String(toRaw).trim(),
        };
      })
      .filter((rule) => rule.from.length > 0 || rule.to.length > 0);
  }

  private normalizeVisibleWhen(
    visibleWhen: { fieldId?: string; values?: unknown; selectedValues?: unknown } | undefined,
  ): { fieldId: string; values: string[] } | undefined {
    if (!visibleWhen || typeof visibleWhen.fieldId !== 'string' || visibleWhen.fieldId === '') {
      return undefined;
    }

    // Multi-checkbox path: selectedValues holds the selections, values is '' placeholder
    const rawSelected = visibleWhen.selectedValues;
    if (Array.isArray(rawSelected) && rawSelected.length > 0) {
      const values = rawSelected
        .map((value) => String(value ?? ''))
        .filter((value) => value.length > 0);
      if (values.length > 0) {
        return { fieldId: visibleWhen.fieldId, values };
      }
    }

    // Text input path: values holds a string or string[]
    const rawValues = visibleWhen.values;
    const values = Array.isArray(rawValues)
      ? rawValues.map((value) => String(value ?? ''))
      : typeof rawValues === 'string'
        ? rawValues
            .split(',')
            .map((value) => value.trim())
            .filter((value) => value.length > 0)
        : [];

    if (values.length === 0) {
      return undefined;
    }

    return {
      fieldId: visibleWhen.fieldId,
      values,
    };
  }

  addField() {
    // Create a new field with a temporary ID
    const newField: FieldDefinition = {
      id: crypto.randomUUID(),
      type: 'text',
      label: '',
      placeholder: '',
      required: false,
      options: [],
      allowMultiple: true,
      allowMultiplePhotos: false,
      filenamePrefix: '',
      min: undefined,
      max: undefined,
      step: undefined,
      pattern: undefined,
      content: '',
      fontSize: 'md',
      fontWeight: 'normal',
      color: '#333',
      textAlign: 'left',
      tag: 'div',
      width: 'auto',
      prefillLastValue: undefined,
      calculationKind: undefined,
      inputFieldAId: undefined,
      inputFieldBId: undefined,
      currentFieldId: undefined,
      checklistFieldId: undefined,
      sourceFieldId: undefined,
      outputFieldId: undefined,
      transitions: [],
      transitionFallback: undefined,
      arithmeticOperator: undefined,
      numberFormat: undefined,
      durationFormat: undefined,
      emptyBehavior: undefined,
      persistSnapshot: undefined,
      durationInputUnit: undefined,
      durationDisplayFormat: undefined,
      visibleWhen: undefined,
      visibleWhenMode: 'none',
    };

    // Open field editor in modal
    this.openFieldEditorModal(newField, this.translationService.translate('formBuilder.newField'));
  }

  toggleFieldActions(index: number): void {
    this.activeMenuIndex.update((current) => (current === index ? null : index));
  }

  closeAllFieldActions(): void {
    this.activeMenuIndex.set(null);
  }

  editField(field: FieldDefinition) {
    this.activeMenuIndex.set(null);
    // Clone to prevent mutation of original field during editing
    const fieldCopy = { ...field };

    // Update the URL to include ?field=:fieldId so the modal is bookmarkable,
    // refresh-safe, and closeable via browser back.
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { field: field.id },
      queryParamsHandling: 'merge',
    });

    this.openFieldEditorModal(
      fieldCopy,
      this.translationService.translate('formBuilder.editField'),
      true, // isUrlDriven
    );
  }

  /**
   * Open the field editor for a field identified by URL param (page refresh / direct link).
   * Finds the field by ID in the current field list and opens its editor.
   * Removes the stale ?field= param if the ID is not found.
   */
  private openFieldByUrl(fieldId: string): void {
    const field = this.fields().find((f) => f.id === fieldId);
    if (!field) {
      // Field not found — stale URL param, remove it silently
      const queryParams = { ...this.route.snapshot.queryParams };
      delete queryParams['field'];
      this.router.navigate([], { relativeTo: this.route, queryParams });
      return;
    }
    const fieldCopy = { ...field };
    this.openFieldEditorModal(
      fieldCopy,
      this.translationService.translate('formBuilder.editField'),
      true, // isUrlDriven
    );
  }

  private async openFieldEditorModal(
    field: FieldDefinition,
    title: string,
    isUrlDriven = false,
  ): Promise<void> {
    // Mark modal as open and store a reference to the close signal so browser-back can close it.
    // For URL-driven modals, also append an "Edit Field" breadcrumb segment.
    if (isUrlDriven) {
      this._isModalOpen = true;
      this.breadcrumbService.setExtraItem({ label: 'Edit Field', url: null });
    }

    // Determine which editor component to use based on field type
    const { component: EditorComponent, configFormFields } = this.getEditorComponentForFieldType(
      field.type,
    );

    // Create writable signal with field copy - this is the parent's source of truth
    // Editor component will update this signal directly as user modifies the field
    const fieldSignal = signal<FieldDefinition>({ ...field });

    // Create signals for controlling button state in this modal instance
    const saveDisabled = signal<string | null>(null);
    const cancelDisabled = signal<string | null>(null);

    // Create signal for field type changes (pure signal-based pattern)
    // Editor writes to this when user selects a different field type
    const typeChangeSignal = signal<{ newType: string; field: FieldDefinition } | null>(null);
    this.logger.debug(
      '[FormBuilder.openFieldEditorModal] 📊 Created typeChangeSignal:',
      typeChangeSignal,
    );
    this.logger.debug(
      '[FormBuilder.openFieldEditorModal] 📊 Initial typeChangeSignal value:',
      typeChangeSignal(),
    );

    // Create closeSignal to decouple modal closing from button handlers
    // When button handlers call closeSignal.set(), it triggers modal closure via DynamicModalDialogComponent
    const closeSignal = signal<{ action: string; data?: any } | null>(null);
    this.logger.debug('[FormBuilder.openFieldEditorModal] 📊 Created closeSignal');

    // Store reference so browser-back (queryParams subscription) can close this modal
    if (isUrlDriven) {
      this._currentModalCloseSignal = closeSignal;
    }

    this.logger.debug(
      '[FormBuilder.openFieldEditorModal] 🔧 Setting up typeChangeSignal effect...',
    );
    runInInjectionContext(this.injector, () => {
      effect(() => {
        this.logger.debug('[FormBuilder.typeChangeEffect] 🔵 Effect triggered');
        const typeChange = typeChangeSignal();
        this.logger.debug(
          '[FormBuilder.typeChangeEffect] 📊 typeChangeSignal() value:',
          typeChange,
        );

        if (typeChange) {
          this.logger.debug('[FormBuilder.typeChangeEffect] 🔄 Type change detected!');
          this.logger.debug('[FormBuilder.typeChangeEffect] 📊 New type:', typeChange.newType);
          this.logger.debug('[FormBuilder.typeChangeEffect] 📊 Field data:', typeChange.field);
          this.logger.debug(
            '[FormBuilder.typeChangeEffect] 🚀 Triggering closeSignal with typeChange action',
          );
          closeSignal.set({ action: 'typeChange', data: typeChange });
          this.logger.debug('[FormBuilder.typeChangeEffect] ✅ closeSignal.set() completed');
        } else {
          this.logger.debug(
            '[FormBuilder.typeChangeEffect] ℹ️ typeChange is null - no action taken',
          );
        }
      });
    });
    this.logger.debug('[FormBuilder.openFieldEditorModal] ✅ typeChangeSignal effect configured');

    // Store for manual reset if needed
    this.modalSaveDisabled = saveDisabled;
    this.modalCancelDisabled = cancelDisabled;

    // Check if this is an existing field (for remove button visibility)
    const isExistingField = this.fields().some((f) => f.id === field.id);

    const config: ModalConfig = {
      component: EditorComponent,
      title,
      size: 'large',
      closeSignal, // ✅ Pass closeSignal for decoupled modal closing
      componentInputs: {
        fieldSignal, // Pass writable signal to editor
        saveDisabled,
        cancelDisabled,
        typeChangeSignal, // Pass type change signal to editor (pure signal pattern)
        configFormFields, // Formly field config for the GenericFieldConfigEditorComponent
        availableFields: this.fields(), // Used by calculated field dependency pickers/validation
      },
      footerClassName: 'flex flex-wrap gap-md flex-responsive-xs col-3',
      actionButtons: [
        {
          id: 'cancel',
          label: this.translationService.translate('button.cancel'),
          variant: 'secondary',
          className: '', // Push cancel button to the left
          testId: `${field.type}-edit-cancel`,
          disabled: cancelDisabled as any, // Signal<string | null> for disabled state
          onClick: async () => {
            // Signal-based pattern: cancel button simply closes modal via closeSignal
            // The cancelDisabled signal is provided for future use if backend operations
            // need to prevent cancellation (e.g., saving in progress)
            this.logger.debug(
              '[FormBuilder.cancelButton] ✅ Triggering closeSignal with cancel action',
            );
            // ✅ Use closeSignal instead of modal.close() - decouples button from modal lifecycle
            closeSignal.set({ action: 'cancel' });
          },
        },
        // Only show remove button for existing fields
        ...(isExistingField
          ? [
              {
                id: 'remove',
                label: this.translationService.translate('button.remove'),
                className: '',
                variant: 'danger' as const,
                onClick: async () => {
                  this.logger.debug(
                    '[FormBuilder.removeButton] 🗑️  Requesting field removal:',
                    field.id,
                  );
                  // Open confirmation modal (stacked modal)
                  const confirmed = await this.showRemoveFieldConfirmation(field.label);
                  if (confirmed) {
                    this.removeField(field.id);
                    closeSignal.set({ action: 'remove' });
                  }
                },
              },
            ]
          : []),
        {
          id: 'save',
          className: 'ml-auto', // Push save button to the right
          label: this.translationService.translate('button.apply'),
          variant: 'primary',
          testId: `${field.type}-edit-save`,
          disabled: saveDisabled as any, // Signal<string | null> for disabled state with message
          onClick: async () => {
            // Signal-based pattern: read modified field directly from writable signal
            // The editor updates this signal during its normal validation cycle.
            // When save is clicked, the signal contains the final modified field value.
            const modifiedField = fieldSignal();
            this.logger.debug(
              '[FormBuilder.saveButton] 📖 Retrieved modified field from signal:',
              modifiedField?.label,
            );

            // ✅ Use closeSignal instead of modal.close() - decouples button from modal lifecycle
            closeSignal.set({ action: 'save', data: modifiedField });
          },
        },
      ],
    };

    const result = await this.modalService.openModal(config);

    // Handle modal close result
    if (result?.action === 'typeChange') {
      // Field type was changed - recursively open modal with new editor component
      const updatedField = fieldSignal();
      this.logger.debug('[FormBuilder.typeChangeHandler] 🔄 Type changed to:', updatedField.type);
      this.logger.debug(
        '[FormBuilder.typeChangeHandler] 🔄 Opening new editor for type:',
        updatedField.type,
      );
      // Keep the same ?field= URL during type switching (same field, new editor).
      // Use replaceUrl to avoid polluting browser history with intermediate type changes.
      if (isUrlDriven) {
        this.router.navigate([], {
          relativeTo: this.route,
          queryParams: { field: updatedField.id },
          queryParamsHandling: 'merge',
          replaceUrl: true,
        });
      }
      // Recursively open modal with the new field (new type will determine new editor component)
      // _isModalOpen remains true; the recursive call owns _currentModalCloseSignal
      this.openFieldEditorModal(
        updatedField,
        `Edit: ${updatedField.label || 'Field'}`,
        isUrlDriven,
      );
    } else {
      // Modal editing session is fully complete (save, cancel, remove, or browser-back)
      if (isUrlDriven) {
        this._isModalOpen = false;
        this._currentModalCloseSignal = null;
        this.breadcrumbService.clearExtraItem();
        // Remove ?field= from the URL only if it is still present
        // (browser back will have already removed it, so we guard against double-navigation)
        const queryParams = { ...this.route.snapshot.queryParams };
        if (queryParams['field']) {
          delete queryParams['field'];
          this.router.navigate([], { relativeTo: this.route, queryParams });
        }
      }
      if (result?.action === 'save' && result.data) {
        this.handleFieldSaved(result.data);
      }
    }

    // Clean up signal references
    this.modalSaveDisabled = null;
    this.modalCancelDisabled = null;
  }

  private handleFieldSaved(field: FieldDefinition) {
    this.logger.debug('[FormBuilder.handleFieldSaved] 💾 ===== PROCESSING FIELD SAVED =====');
    this.logger.debug('[FormBuilder.handleFieldSaved] Field label:', field.label);

    const currentFields = this.fields();
    this.logger.debug(
      '[FormBuilder.handleFieldSaved] Current field count in memory:',
      currentFields.length,
    );

    const existingIndex = currentFields.findIndex((f) => f.id === field.id);
    this.logger.debug(
      '[FormBuilder.handleFieldSaved] Found existing field at index:',
      existingIndex,
    );

    if (existingIndex >= 0) {
      // Update existing field
      this.logger.debug(
        '[FormBuilder.handleFieldSaved] ✏️  UPDATING existing field at index:',
        existingIndex,
      );
      const updatedFields = [...currentFields];
      updatedFields[existingIndex] = field;
      this.fields.set(updatedFields);
      this.logger.debug(
        '[FormBuilder.handleFieldSaved] ✅ Updated in-memory signal. New field data:',
        field,
      );
      this.logger.log(`Field updated: ${field.label}`);
    } else {
      // Add new field
      this.logger.debug('[FormBuilder.handleFieldSaved] ➕ ADDING new field');
      this.fields.set([...currentFields, field]);
      this.logger.debug(
        '[FormBuilder.handleFieldSaved] ✅ Added to in-memory signal. New field data:',
        field,
      );
      this.logger.log(`Field added: ${field.label}`);
    }

    // Emit change event
    this.logger.debug(
      '[FormBuilder.handleFieldSaved] 🔔 Emitting fieldsChanged event to parent component...',
    );
    this.emitChanges();
    this.logger.debug(
      '[FormBuilder.handleFieldSaved] ⚠️  NOTE: Data is still in-memory only. Parent must save to database!',
    );
  }

  private removeField(fieldId: string) {
    this.logger.debug('[FormBuilder.removeField] 🗑️  REMOVING field with ID:', fieldId);
    const currentFields = this.fields();
    const updatedFields = currentFields.filter((f) => f.id !== fieldId);
    this.fields.set(updatedFields);
    this.logger.debug('[FormBuilder.removeField] ✅ Field removed from in-memory signal');
    this.logger.log('Field removed');
    this.emitChanges();
  }

  private async showRemoveFieldConfirmation(fieldLabel: string): Promise<boolean> {
    // Create closeSignal for this confirmation modal (stacked on top of field editor modal)
    const closeSignal = signal<{ action: string; data?: any } | null>(null);

    const config: ModalConfig = {
      component: RemoveFieldConfirmationComponent,
      componentInputs: {
        fieldLabel,
      },
      title: this.translationService.translate('formBuilder.deleteField.title'),
      size: 'small',
      footerClassName: 'flex flex-wrap gap-md flex-responsive-xs col-2',
      closeSignal,
      actionButtons: [
        {
          id: 'cancel',
          label: this.translationService.translate('button.cancel'),
          variant: 'secondary',
          onClick: async () => {
            closeSignal.set({ action: 'cancel' });
          },
        },
        {
          id: 'confirm',
          className: 'ml-auto',
          label: this.translationService.translate('formBuilder.deleteField.button'),
          variant: 'danger',
          onClick: async () => {
            closeSignal.set({ action: 'confirm' });
          },
        },
      ],
    };

    const result = await this.modalService.openModal(config);
    return result?.action === 'confirm';
  }

  moveFieldUp(index: number) {
    if (index === 0) return;
    const fields = [...this.fields()];
    [fields[index - 1], fields[index]] = [fields[index], fields[index - 1]];
    this.fields.set(fields);
    this.activeMenuIndex.set(null);
    this.emitChanges();
  }

  moveFieldDown(index: number) {
    const fields = this.fields();
    if (index === fields.length - 1) return;
    const updated = [...fields];
    [updated[index], updated[index + 1]] = [updated[index + 1], updated[index]];
    this.fields.set(updated);
    this.activeMenuIndex.set(null);
    this.emitChanges();
  }

  onItemReordered(event: DragReorderEvent): void {
    this.reorderField(event.fromIndex, event.toIndex);
  }

  private reorderField(fromIndex: number, toIndex: number) {
    const fields = [...this.fields()];
    const [moved] = fields.splice(fromIndex, 1);
    fields.splice(toIndex, 0, moved);
    this.fields.set(fields);
    this.emitChanges();
  }

  private emitChanges() {
    const formlyFields = this.fields().map((field) => this.fieldDefToFormly(field));
    this.fieldsChanged.emit(formlyFields);
  }

  /**
   * Helper function to add validators to a Formly field config
   */
  private addValidators(config: FormlyFieldConfig, field: FieldDefinition): FormlyFieldConfig {
    // Apply layout width: 'full' spans the entire row regardless of the form's column count.
    if (field.width === 'full') {
      config.className = (config.className ? config.className + ' ' : '') + 'flex-basis-full';
    }

    if (field.required) {
      // Formly validator for required fields
      const requiredValidator = (control: AbstractControl) => {
        if (!control.value) {
          return { required: { valid: false } };
        }
        return null;
      };

      config.validators = {
        ...config.validators,
        required: requiredValidator,
      };
    }

    return config;
  }

  private fieldDefToFormly(field: FieldDefinition): FormlyFieldConfig {
    // Handle radio and checkbox-group fields
    if (field.type === 'radio' || field.type === 'choice') {
      // 'choice' is legacy - treat as radio for backward compatibility
      const config: FormlyFieldConfig = {
        key: field.id,
        type: 'radio',
        props: {
          label: field.label,
          required: field.required,
          options: field.options || [],
          optionsSource: field.optionsSource || 'inline',
          lookupListId: field.lookupListId,
          prefillLastValue: field.prefillLastValue,
          visibleWhen: this.normalizeVisibleWhen(
            field.visibleWhenMode === 'show_when_value' ? field.visibleWhen : undefined,
          ),
        },
      };
      (config as any).id = field.id || crypto.randomUUID();
      return this.addValidators(config, field);
    }

    if (field.type === 'checkbox-group') {
      const config: FormlyFieldConfig = {
        key: field.id,
        type: 'checkbox-group',
        props: {
          label: field.label,
          required: field.required,
          options: field.options || [],
          multiple: field.allowMultiple !== false, // Default to true
          optionsSource: field.optionsSource || 'inline',
          lookupListId: field.lookupListId,
          prefillLastValue: field.prefillLastValue,
          visibleWhen: this.normalizeVisibleWhen(
            field.visibleWhenMode === 'show_when_value' ? field.visibleWhen : undefined,
          ),
        },
      };
      (config as any).id = field.id || crypto.randomUUID();
      return this.addValidators(config, field);
    }

    if (field.type === 'select') {
      const config: FormlyFieldConfig = {
        key: field.id,
        type: 'select',
        props: {
          label: field.label,
          placeholder: field.placeholder,
          required: field.required,
          options: field.options || [],
          multiple: field.allowMultiple || false, // Default to false for select
          optionsSource: field.optionsSource || 'inline',
          lookupListId: field.lookupListId,
          prefillLastValue: field.prefillLastValue,
          visibleWhen: this.normalizeVisibleWhen(
            field.visibleWhenMode === 'show_when_value' ? field.visibleWhen : undefined,
          ),
        },
      };
      (config as any).id = field.id || crypto.randomUUID();
      return this.addValidators(config, field);
    }

    if (field.type === 'dropdown') {
      const config: FormlyFieldConfig = {
        key: field.id,
        type: 'dropdown',
        props: {
          label: field.label,
          placeholder: field.placeholder,
          required: field.required,
          options: field.options || [],
          multiple: field.allowMultiple || false,
          optionsSource: field.optionsSource || 'inline',
          lookupListId: field.lookupListId,
          prefillLastValue: field.prefillLastValue,
          visibleWhen: this.normalizeVisibleWhen(
            field.visibleWhenMode === 'show_when_value' ? field.visibleWhen : undefined,
          ),
        },
      };
      (config as any).id = field.id || crypto.randomUUID();
      return this.addValidators(config, field);
    }

    if (field.type === 'duration') {
      const config: FormlyFieldConfig = {
        key: field.id,
        type: 'duration',
        props: {
          label: field.label,
          placeholder: field.placeholder,
          required: field.required,
          durationInputUnit: field.durationInputUnit || 'minutes',
          durationDisplayFormat: field.durationDisplayFormat || 'short',
          prefillLastValue: field.prefillLastValue,
          visibleWhen: this.normalizeVisibleWhen(
            field.visibleWhenMode === 'show_when_value' ? field.visibleWhen : undefined,
          ),
        },
      };
      (config as any).id = field.id || crypto.randomUUID();
      return this.addValidators(config, field);
    }

    if (field.type === 'calculated') {
      const transitions = this.editorTransitionsToFormlyTransitions(field.transitions);
      const config: FormlyFieldConfig = {
        key: field.id,
        type: 'calculated',
        props: {
          label: field.label,
          required: field.required,
          calculationKind: field.calculationKind,
          inputFieldAId: field.inputFieldAId,
          inputFieldBId: field.inputFieldBId,
          currentFieldId: field.currentFieldId,
          checklistFieldId: field.checklistFieldId,
          sourceFieldId: field.sourceFieldId,
          outputFieldId: field.outputFieldId,
          transitions,
          transitionFallback: field.transitionFallback,
          arithmeticOperator: field.arithmeticOperator,
          numberFormat: field.numberFormat,
          durationFormat: field.durationFormat,
          emptyBehavior: field.emptyBehavior,
          // v1 behavior is always persisted snapshot for calculated values.
          persistSnapshot: true,
          prefillLastValue: field.prefillLastValue,
          visibleWhen: this.normalizeVisibleWhen(
            field.visibleWhenMode === 'show_when_value' ? field.visibleWhen : undefined,
          ),
        },
      };
      (config as any).id = field.id || crypto.randomUUID();
      return this.addValidators(config, field);
    }

    // Handle other field types
    const config: FormlyFieldConfig = {
      key: field.id,
      type: field.type,
      props: {
        label: field.label,
        placeholder: field.placeholder,
        required: field.required,
        prefillLastValue: field.prefillLastValue,
        visibleWhen: this.normalizeVisibleWhen(
          field.visibleWhenMode === 'show_when_value' ? field.visibleWhen : undefined,
        ),
      },
    };

    if (field.type === 'textarea' && field.rows) {
      config.props!['rows'] = field.rows;
    }

    // Add numeric field properties
    if (field.type === 'number') {
      if (field.min !== undefined) config.props!['min'] = field.min;
      if (field.max !== undefined) config.props!['max'] = field.max;
      if (field.step !== undefined) config.props!['step'] = field.step;
    }

    // Add pattern validation for email/tel
    if (field.type === 'email' || field.type === 'tel') {
      if (field.pattern) config.props!['pattern'] = field.pattern;
    }

    // Add photo field options
    if (field.type === 'photo') {
      config.props!['allowMultiplePhotos'] = field.allowMultiplePhotos || false;
      config.props!['filenamePrefix'] = field.filenamePrefix || '';
    }

    // Add text-label field options
    if (field.type === 'text-label') {
      config.props!['content'] = field.content || '';
      config.props!['fontSize'] = field.fontSize || 'md';
      config.props!['fontWeight'] = field.fontWeight || 'normal';
      config.props!['color'] = field.color || '#333';
      config.props!['textAlign'] = field.textAlign || 'left';
      config.props!['tag'] = field.tag || 'div';
    }

    // Use the stored UUID as the HTML element id — never derive it from the
    // human-readable key, as that leaks semantic meaning to the browser and
    // causes autofill cross-population between fields (e.g. a text field
    // keyed "email" getting browser-autofilled as an email input).
    // Fall back to a fresh UUID for legacy fields that pre-date id storage.
    (config as any).id = field.id || crypto.randomUUID();

    return this.addValidators(config, field);
  }
}
